import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { invoicesAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Upload, Trash2, Play, FileText, CheckCircle, Clock, AlertTriangle, Loader2 } from 'lucide-react';

const ENERGY_TYPES = ['Electricity', 'Natural Gas', 'Diesel', 'Steam', 'Compressed Air'];
const ACCEPTED_EXT = ['.pdf', '.csv', '.xlsx', '.xls', '.png', '.jpg', '.jpeg'];
const MAX_FILE_BYTES = 15 * 1024 * 1024;

function extOf(name = '') {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot).toLowerCase() : '';
}

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [form, setForm] = useState({
    energy_type: 'Electricity',
    consumption: 0,
    cost: 0,
    date: new Date().toISOString().split('T')[0],
  });
  const fileRef = useRef(null);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const r = await invoicesAPI.getAll();
      setInvoices(r.data || []);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to load invoices';
      toast.error(msg);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInvoices(); }, []);

  const validateFile = (file) => {
    if (!file) return 'No file selected';
    const ext = extOf(file.name);
    if (!ACCEPTED_EXT.includes(ext)) {
      return `File type "${ext || 'unknown'}" not allowed. Accepted: ${ACCEPTED_EXT.join(', ')}`;
    }
    if (file.size > MAX_FILE_BYTES) return 'File too large (max 15 MB)';
    return null;
  };

  const openDialogWithFile = (file) => {
    const err = validateFile(file);
    if (err) { toast.error(err); return; }
    setSelectedFile(file);
    setForm({
      energy_type: 'Electricity',
      consumption: 0,
      cost: 0,
      date: new Date().toISOString().split('T')[0],
    });
    setDialogOpen(true);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) openDialogWithFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) { toast.error('Please select a file'); return; }
    try {
      setUploading(true);
      await invoicesAPI.upload(selectedFile, form);
      toast.success(`Uploaded ${selectedFile.name}`);
      setDialogOpen(false);
      setSelectedFile(null);
      if (fileRef.current) fileRef.current.value = '';
      await fetchInvoices();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Upload failed';
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) openDialogWithFile(file);
  };

  const handleProcessAll = async () => {
    try {
      const r = await invoicesAPI.processAll();
      toast.success(`Processed ${r.data?.processed ?? 0} invoices`);
      await fetchInvoices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to process invoices');
    }
  };

  const handleDelete = async (id, fileName) => {
    try {
      await invoicesAPI.delete(id);
      toast.success(`Deleted ${fileName}`);
      await fetchInvoices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const pending = invoices.filter(i => i.status === 'pending').length;

  return (
    <div data-testid="invoices-page" className="space-y-6">
      <div className="cp-page-header">
        <h1 className="cp-page-title">Invoices</h1>
        {pending > 0 && (
          <Button onClick={handleProcessAll} className="bg-primary hover:bg-primary/90" data-testid="process-all-btn">
            <Play className="w-4 h-4 mr-2" /> Process All ({pending})
          </Button>
        )}
      </div>

      <div
        className="flex items-start gap-3 rounded-lg border border-amber-300/60 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-400/30 dark:bg-amber-950/40 dark:text-amber-200"
        data-testid="ai-disclaimer-invoices"
      >
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <p>
          <span className="font-semibold">AI estimates are indicative only.</span>{' '}
          Auto-extracted consumption, energy type and emission-factor suggestions are statistical estimates. Always verify the figures against the original invoice before approving them for CBAM or audit use.
        </p>
      </div>

      {/* Real file input (hidden) + drop zone */}
      <input
        ref={fileRef}
        type="file"
        accept={ACCEPTED_EXT.join(',')}
        onChange={handleFileInputChange}
        className="hidden"
        data-testid="invoice-file-input"
      />
      <div
        className={`cp-upload-zone ${dragging ? 'dragging' : ''}`}
        data-testid="upload-zone"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileRef.current?.click()}
        role="button"
        tabIndex={0}
      >
        <Upload className="w-10 h-10 text-muted-foreground mb-3" />
        <p className="text-sm font-medium mb-1">Drop invoice file here or click to browse</p>
        <p className="text-xs text-muted-foreground">PDF, CSV, Excel, PNG or JPG · max 15 MB</p>
      </div>

      <div className="cp-table-wrapper animate-fade-in">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>File Name</TableHead>
              <TableHead>Energy Type</TableHead>
              <TableHead className="text-right">Consumption</TableHead>
              <TableHead className="text-right">Cost</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Confidence</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              [...Array(3)].map((_, i) => (
                <TableRow key={`sk-${i}`}>{[...Array(8)].map((_, j) => <TableCell key={j}><div className="h-4 bg-muted rounded animate-pulse" /></TableCell>)}</TableRow>
              ))
            ) : invoices.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" /> No invoices uploaded
              </TableCell></TableRow>
            ) : invoices.map(inv => (
              <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                <TableCell className="font-medium text-sm">{inv.file_name}</TableCell>
                <TableCell><span className="cp-badge-indigo">{inv.energy_type}</span></TableCell>
                <TableCell className="text-right font-mono text-sm">{Number(inv.consumption || 0).toLocaleString()}</TableCell>
                <TableCell className="text-right font-mono text-sm">${Number(inv.cost || 0).toLocaleString()}</TableCell>
                <TableCell className="text-sm">{inv.date}</TableCell>
                <TableCell>
                  <span className={`inline-flex items-center gap-1 ${inv.status === 'processed' ? 'cp-badge-green' : 'cp-badge-amber'}`}>
                    {inv.status === 'processed' ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                    {inv.status}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2 min-w-[100px]">
                    <Progress value={(inv.confidence_score || 0) * 100} className="h-1.5 flex-1" />
                    <span className="text-xs font-mono">{((inv.confidence_score || 0) * 100).toFixed(0)}%</span>
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(inv.id, inv.file_name)}
                    className="text-destructive hover:text-destructive"
                    data-testid={`delete-invoice-${inv.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!uploading) setDialogOpen(o); }}>
        <DialogContent data-testid="invoice-dialog">
          <DialogHeader><DialogTitle>Upload invoice</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md bg-muted/50 px-3 py-2 text-sm flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              <span className="truncate flex-1" data-testid="invoice-selected-file">
                {selectedFile?.name || 'No file selected'}
              </span>
              <span className="text-xs text-muted-foreground font-mono">
                {selectedFile ? `${(selectedFile.size / 1024).toFixed(1)} KB` : ''}
              </span>
            </div>
            <div>
              <Label>Energy Type</Label>
              <Select value={form.energy_type} onValueChange={v => setForm({ ...form, energy_type: v })}>
                <SelectTrigger data-testid="invoice-energy-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ENERGY_TYPES.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Consumption</Label><Input type="number" value={form.consumption} onChange={e => setForm({ ...form, consumption: Number(e.target.value) })} data-testid="invoice-consumption-input" /></div>
              <div><Label>Cost ($)</Label><Input type="number" value={form.cost} onChange={e => setForm({ ...form, cost: Number(e.target.value) })} data-testid="invoice-cost-input" /></div>
            </div>
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="invoice-date-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={uploading} onClick={() => setDialogOpen(false)} data-testid="invoice-cancel-btn">Cancel</Button>
            <Button
              disabled={uploading || !selectedFile}
              onClick={handleUpload}
              className="bg-primary hover:bg-primary/90"
              data-testid="invoice-submit-btn"
            >
              {uploading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading…</>) : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
